import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Truck, RefreshCw, MapPin, Phone, Navigation, Clock,
  Loader2, Signal, SignalZero
} from 'lucide-react';
import api from '../services/api';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom driver marker icon
const createDriverIcon = (status, hasLocation) => {
  const color = status === 'on_load' ? '#4ADE80' : status === 'available' ? '#3B82F6' : '#6B7280';
  const opacity = hasLocation ? 1 : 0.5;
  
  return L.divIcon({
    className: 'driver-marker',
    html: `
      <div style="
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: ${color};
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: ${opacity};
      ">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <path d="M10 17h4V5H2v12h3m5 0a3 3 0 1 0 6 0m-6 0a3 3 0 1 1 6 0"/>
          <path d="M14 17h3v-5h2.5l3 5H14"/>
        </svg>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18]
  });
};

// Map bounds auto-fit component
const FitBounds = ({ drivers }) => {
  const map = useMap();
  
  useEffect(() => {
    const driversWithLocation = drivers.filter(d => d.location?.latitude && d.location?.longitude);
    if (driversWithLocation.length > 0) {
      const bounds = L.latLngBounds(
        driversWithLocation.map(d => [d.location.latitude, d.location.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
    }
  }, [drivers, map]);
  
  return null;
};

const DriverLocationMap = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await api.get('/dispatch/drivers/locations');
      setDrivers(res.data.drivers || []);
      setLastUpdate(new Date(res.data.updated_at));
    } catch (error) {
      console.error('Failed to fetch driver locations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(fetchLocations, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLocations]);

  const driversWithLocation = drivers.filter(d => d.location?.latitude && d.location?.longitude);
  const driversWithoutLocation = drivers.filter(d => !d.location?.latitude);

  const formatTime = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  // Default center (US)
  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;

  if (loading) {
    return (
      <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#4ADE80]" />
            Live Driver Tracking
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'text-[#4ADE80]' : 'text-gray-400'}
            >
              {autoRefresh ? <Signal className="w-4 h-4" /> : <SignalZero className="w-4 h-4" />}
              <span className="ml-1 text-xs">{autoRefresh ? 'Live' : 'Paused'}</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchLocations}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {lastUpdate && (
          <p className="text-xs text-gray-500">
            Updated: {formatTime(lastUpdate.toISOString())}
          </p>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {/* Legend */}
        <div className="flex items-center gap-4 mb-3 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-[#4ADE80]" />
            <span className="text-gray-400">On Load ({driversWithLocation.filter(d => d.status === 'on_load').length})</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-[#3B82F6]" />
            <span className="text-gray-400">Available ({driversWithLocation.filter(d => d.status === 'available').length})</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-500 opacity-50" />
            <span className="text-gray-400">No GPS ({driversWithoutLocation.length})</span>
          </div>
        </div>

        {/* Map */}
        <div className="h-[400px] rounded-lg overflow-hidden border border-[#3B82F6]/20">
          <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
            
            {driversWithLocation.length > 0 && <FitBounds drivers={driversWithLocation} />}
            
            {driversWithLocation.map((driver) => (
              <Marker
                key={driver.id}
                position={[driver.location.latitude, driver.location.longitude]}
                icon={createDriverIcon(driver.status, true)}
                eventHandlers={{
                  click: () => setSelectedDriver(driver)
                }}
              >
                <Popup className="driver-popup">
                  <div className="min-w-[200px]">
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="w-4 h-4 text-[#4ADE80]" />
                      <span className="font-bold text-sm">{driver.name}</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <p className={`font-medium ${
                        driver.status === 'on_load' ? 'text-green-600' : 'text-blue-600'
                      }`}>
                        {driver.status === 'on_load' ? 'On Load' : 'Available'}
                      </p>
                      {driver.assignment && (
                        <div className="bg-gray-100 rounded p-1.5 mt-1">
                          <p className="text-gray-600">
                            <Navigation className="w-3 h-3 inline mr-1" />
                            {driver.assignment.pickup_location?.split(',')[0]} → {driver.assignment.delivery_location?.split(',')[0]}
                          </p>
                          <p className="text-gray-500 capitalize">
                            Status: {driver.assignment.status?.replace('_', ' ')}
                          </p>
                        </div>
                      )}
                      <p className="text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(driver.location.updated_at)}
                      </p>
                      {driver.location.speed && (
                        <p className="text-gray-500">
                          Speed: {Math.round(driver.location.speed)} mph
                        </p>
                      )}
                      {driver.phone && (
                        <a 
                          href={`tel:${driver.phone}`}
                          className="text-blue-600 flex items-center gap-1"
                        >
                          <Phone className="w-3 h-3" />
                          {driver.phone}
                        </a>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Drivers without GPS */}
        {driversWithoutLocation.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-gray-500 mb-2">Drivers without recent GPS data:</p>
            <div className="flex flex-wrap gap-2">
              {driversWithoutLocation.map(driver => (
                <div 
                  key={driver.id}
                  className="flex items-center gap-1 bg-[#0A1628] px-2 py-1 rounded text-xs"
                >
                  <div className={`w-2 h-2 rounded-full ${
                    driver.status === 'on_load' ? 'bg-[#4ADE80]' : 
                    driver.status === 'available' ? 'bg-[#3B82F6]' : 'bg-gray-500'
                  } opacity-50`} />
                  <span className="text-gray-400">{driver.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {drivers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No drivers found</p>
            <p className="text-xs">Add drivers in Driver Management to see them on the map</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DriverLocationMap;
